import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  PiFileTextLight,
  PiMagnifyingGlassLight,
  PiSlidersHorizontalLight,
  PiInfoLight,
} from 'react-icons/pi';

import { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import { ClientSelectorClient } from '../shared/ClientSelector';
import ClientSelector from '../shared/ClientSelector';
import { Collaborator } from '../shared/CollaboratorSelector';
import FileOrDescriptionSelector from '../shared/FileOrDescriptionSelector';
import IndexSourceSelector from './IndexSourceSelector';
import { hasCasesSupport } from './matterConstants';
import Toggle from '../shared/TailwindComponents/Toggle';
import {
  BaseForm,
  BaseFormFieldConfig,
  BaseFormSectionConfig,
  WizardStepRail,
} from '../shared/FormComponents';
import GlassCard from '../shared/brand/GlassCard';
import { InfoRow, Well } from '../shared/brand/Field';
import { SELECTED_WELL } from '../shared/brand/selectedCard';
import { FormSchema, FormField } from '../../utils/validation/form-schema';
import { useAppDispatch } from '../../store';
import { isMatterEconomicsEnabled } from '../../utils/config';
import MatterPricingFields from './economics/MatterPricingFields';
import {
  MatterPricingValue,
  emptyMatterPricing,
  getMatterPricingErrors,
} from './economics/economicsConstants';
import { showModal, hideModal } from '../../store/actions/modalActions';
import {
  PiScalesLight,
  PiGavelLight,
  PiDatabaseLight,
  PiListChecksLight,
  PiChatTextLight,
  PiTextAlignLeftLight,
  PiNoteLight,
  PiSparkleLight,
  PiTrashLight,
} from 'react-icons/pi';
import { WizardStepTip, FeatureTour, matterWizardStepTips, matterWizardTourSteps } from '../shared/FeatureTour';
import { useFeatureTour } from '../../hooks/useFeatureTour';

// ─── Types ───────────────────────────────────────────────────────────

type WizardStep = 'basics' | 'documents' | 'research' | 'review';

interface AnalysisOptionsValue {
  fetchLaws: boolean;
  fetchPrecedents: boolean;
  fetchDataSource: boolean;
  createTasks: boolean;
  createQuestionnaire: boolean;
  generateDescription: boolean;
  generateNotes: boolean;
  generateDraftSuggestions: boolean;
  generateInsightSuggestions: boolean;
}

const DEFAULT_ANALYSIS_OPTIONS: AnalysisOptionsValue = {
  fetchLaws: true,
  fetchPrecedents: true,
  fetchDataSource: true,
  createTasks: true,
  createQuestionnaire: true,
  generateDescription: true,
  generateNotes: true,
  generateDraftSuggestions: true,
  generateInsightSuggestions: true,
};

export interface MatterWizardData {
  matterTitle: string;
  category: string;
  clients: ClientSelectorClient[];
  collaborators: Collaborator[];
  filesOrDescription: { files: File[]; description: string };
  language: string;
  jurisdiction: string;
  selectedIndexes: string[];
  dataSources: string[];
  analysisOptions: AnalysisOptionsValue;
  projectUUID?: string;
  pricing?: MatterPricingValue;
}

/**
 * Adapter so the reusable MatterPricingFields plugs into BaseForm's custom-field
 * contract (value/onChange). Shows inline validation errors once touched.
 */
const WizardPricingField: React.FC<{
  value?: MatterPricingValue;
  onChange: (v: MatterPricingValue) => void;
  defaultCurrency?: string;
}> = ({ value, onChange, defaultCurrency }) => {
  const [touched, setTouched] = useState(false);
  const v = value || emptyMatterPricing(defaultCurrency || 'usd');
  return (
    <MatterPricingFields
      value={v}
      onChange={(next) => { setTouched(true); onChange(next); }}
      showErrors={touched}
    />
  );
};

interface MatterWizardProps {
  initialTitle?: string;
  categories: DropdownOption[];
  languages: DropdownOption[];
  jurisdictions: DropdownOption[];
  clientOptions: DropdownOption[];
  clientRoleOptions: DropdownOption[];
  dataSourceOptions: DropdownOption[];
  projectOptions?: DropdownOption[];
  defaultLanguage: string;
  defaultJurisdiction: string;
  /** Org/user default currency used to seed the pricing section (US #2250). */
  defaultCurrency?: string;
  autoAddClient: { ClientID: string; ClientRoles: string[] } | null;
  onAddNewClient: () => void;
  onSubmit: (data: MatterWizardData) => void;
  onCancel: () => void;
  onBack: () => void;
  isSubmitting?: boolean;
}

// ─── Step definitions ────────────────────────────────────────────────

/** Pinned so the canvas cannot resize as the user moves between steps. */
const WIZARD_MAX_WIDTH = 'max-w-3xl';

const STEPS: { key: WizardStep; label: string; icon: React.ReactNode }[] = [
  { key: 'basics', label: 'Details', icon: <PiInfoLight className="w-4 h-4" /> },
  { key: 'documents', label: 'Documents', icon: <PiFileTextLight className="w-4 h-4" /> },
  { key: 'research', label: 'Sources', icon: <PiMagnifyingGlassLight className="w-4 h-4" /> },
  { key: 'review', label: 'Review & Launch', icon: <PiSlidersHorizontalLight className="w-4 h-4" /> },
];

// ─── Review panel: custom component for BaseForm ─────────────────────

interface AnalysisOptionsPanelProps {
  value: AnalysisOptionsValue;
  onChange: (val: AnalysisOptionsValue) => void;
  jurisdiction: string;
  hasDataSources: boolean;
  summary: {
    title: string;
    docCount: number;
    jurisdiction: string;
    jurisdictionLabel: string;
    language: string;
    languageLabel: string;
    clientCount: number;
    collaboratorCount: number;
    indexCount: number;
    dataSourceCount: number;
    category: string;
  };
}

const AnalysisOptionsPanel: React.FC<AnalysisOptionsPanelProps> = ({
  value,
  onChange,
  jurisdiction,
  hasDataSources,
  summary,
}) => {
  const { t } = useTranslation();
  const showCases = hasCasesSupport(jurisdiction);

  const toggleOption = (key: keyof AnalysisOptionsValue) => {
    onChange({ ...value, [key]: !value[key] });
  };

  type ToggleOpt = { key: keyof AnalysisOptionsValue; label: string; description: string; icon: React.ReactNode };

  const researchOpts: ToggleOpt[] = [
    { key: 'fetchLaws', label: t('Applicable Laws'), description: t('Search for statutes, regulations, and legal provisions relevant to your matter.'), icon: <PiScalesLight className="w-5 h-5" /> },
    ...(showCases ? [{ key: 'fetchPrecedents' as const, label: t('Precedents / Case Law'), description: t('Find court rulings and judicial decisions that may apply to your matter.'), icon: <PiGavelLight className="w-5 h-5" /> }] : []),
    ...(hasDataSources ? [{ key: 'fetchDataSource' as const, label: t('Connected Data Sources'), description: t('Search your own uploaded document collections for relevant information.'), icon: <PiDatabaseLight className="w-5 h-5" /> }] : []),
  ];

  const genOpts: ToggleOpt[] = [
    { key: 'createTasks', label: t('Suggested Tasks'), description: t('Generate a prioritized action plan with recommended next steps.'), icon: <PiListChecksLight className="w-5 h-5" /> },
    { key: 'createQuestionnaire', label: t('Client Questionnaire'), description: t('Create a tailored set of questions to gather key information from your client.'), icon: <PiChatTextLight className="w-5 h-5" /> },
    { key: 'generateDescription', label: t('Matter Description'), description: t('Produce a concise summary describing the scope and context of the matter.'), icon: <PiTextAlignLeftLight className="w-5 h-5" /> },
    { key: 'generateNotes', label: t('Case Notes'), description: t('Extract key facts, dates, and observations into structured case notes.'), icon: <PiNoteLight className="w-5 h-5" /> },
    { key: 'generateDraftSuggestions', label: t('Draft Suggestions'), description: t('Suggest document drafts such as letters, contracts, or legal briefs.'), icon: <PiFileTextLight className="w-5 h-5" /> },
    { key: 'generateInsightSuggestions', label: t('Insight Suggestions'), description: t('Surface strategic insights and potential risks based on the analysis.'), icon: <PiSparkleLight className="w-5 h-5" /> },
  ];

  const renderToggleCard = (opt: ToggleOpt) => (
    <Well
      key={opt.key}
      className={clsx(
        'flex items-start justify-between gap-3 transition-all duration-200',
        value[opt.key] && SELECTED_WELL
      )}
    >
      <div className="flex items-start gap-3 min-w-0">
        <div className={clsx(
          'flex-shrink-0 mt-0.5 transition-colors duration-200',
          value[opt.key] ? 'text-fg-body' : 'text-fg-muted'
        )}>
          {opt.icon}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-fg-strong">{opt.label}</p>
          {/* `line-clamp-2` cuts the description off with no way to read the
              rest — and a Bulgarian sentence is routinely longer than the
              English it was measured against, so the clamp bites more often
              here than it ever did in English. The title attribute is the
              whole text, on hover and to a screen reader. */}
          <p className="text-xs text-fg-muted mt-0.5 line-clamp-2" title={opt.description}>
            {opt.description}
          </p>
        </div>
      </div>
      <div className="flex-shrink-0 mt-0.5">
        <Toggle enabled={value[opt.key]} onToggle={() => toggleOption(opt.key)} />
      </div>
    </Well>
  );

  return (
    <div className="space-y-6">
      {/* Summary */}
      <GlassCard title={t('Summary')} data-tour-id="review-summary">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
          <InfoRow label={t('Title')} value={summary.title} />
          {summary.category && <InfoRow label={t('Category')} value={summary.category} />}
          <InfoRow
            label={t('Documents')}
            value={summary.docCount > 0
              ? (summary.docCount === 1 ? t('1 file') : t('{{count}} files', { count: summary.docCount }))
              : t('Text description')}
          />
          {/* Unset is a valid answer here, so say so rather than showing a
              blank row that reads as a rendering bug. */}
          <InfoRow label={t('Jurisdiction')} value={summary.jurisdictionLabel || t('Not set')} />
          <InfoRow label={t('Language')} value={summary.languageLabel || t('Not set')} />
          <InfoRow
            label={t('Legal Sources')}
            value={summary.indexCount > 0
              ? (summary.indexCount === 1 ? t('1 source') : t('{{count}} sources', { count: summary.indexCount }))
              : t('None selected')}
          />
          {summary.dataSourceCount > 0 && (
            <InfoRow
              label={t('Data Sources')}
              value={summary.dataSourceCount === 1
                ? t('1 data source')
                : t('{{count}} data sources', { count: summary.dataSourceCount })}
            />
          )}
          {summary.clientCount > 0 && (
            <InfoRow
              label={t('Clients')}
              value={summary.clientCount === 1
                ? t('1 client')
                : t('{{count}} clients', { count: summary.clientCount })}
            />
          )}
          {summary.collaboratorCount > 0 && (
            <InfoRow
              label={t('Access')}
              value={summary.collaboratorCount === 1
                ? t('1 person')
                : t('{{count}} people', { count: summary.collaboratorCount })}
            />
          )}
        </div>
      </GlassCard>

      {/* Research toggles */}
      <GlassCard title={t('Legal Research')} data-tour-id="review-research">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {researchOpts.map(renderToggleCard)}
        </div>
      </GlassCard>

      {/* Generation toggles */}
      <GlassCard title={t('PONS Generation')} data-tour-id="review-generation">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {genOpts.map(renderToggleCard)}
        </div>
      </GlassCard>
    </div>
  );
};

// ─── Component ───────────────────────────────────────────────────────

const MatterWizard: React.FC<MatterWizardProps> = ({
  initialTitle = '',
  categories,
  languages,
  jurisdictions,
  clientOptions,
  clientRoleOptions,
  dataSourceOptions,
  projectOptions = [],
  defaultLanguage,
  defaultJurisdiction,
  defaultCurrency,
  autoAddClient,
  onAddNewClient,
  onSubmit,
  onCancel,
  onBack,
  isSubmitting,
}) => {
  const pricingEnabled = isMatterEconomicsEnabled();
  const pricingCurrency = defaultCurrency || 'usd';
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // Wizard navigation (declared before tour so we can pass currentStep)
  const [currentStep, setCurrentStep] = useState<WizardStep>('basics');
  const currentIdx = STEPS.findIndex((s) => s.key === currentStep);

  // ── Per-step guided tour ──
  const currentTourSteps = matterWizardTourSteps[currentStep] || [];
  const {
    isRunning: isWizardTourRunning,
    handleJoyrideCallback: handleWizardTourCallback,
  } = useFeatureTour({
    tourId: `matter-wizard-tour-${currentStep}`,
    steps: currentTourSteps,
    readyToShow: currentTourSteps.length > 0,
    autoStartDelay: 600,
  });

  // Track whether any field has been touched across all steps
  const wizardDirtyRef = useRef(false);

  // Accumulated form data across steps
  const [accumulated, setAccumulated] = useState<Partial<MatterWizardData>>({
    matterTitle: initialTitle,
    category: categories[0]?.value || '',
    clients: [],
    collaborators: [],
    filesOrDescription: { files: [], description: '' },
    // No language fallback either — whichever language happened to sort first
    // is not an answer, it is a coin flip wearing an answer's clothes.
    language: defaultLanguage || '',
    // No country fallback: the user's preference or nothing at all. An
    // arbitrary first-in-the-list jurisdiction is a wrong answer wearing a
    // right answer's clothes.
    jurisdiction: defaultJurisdiction || '',
    selectedIndexes: [],
    dataSources: [],
    analysisOptions: DEFAULT_ANALYSIS_OPTIONS,
    projectUUID: '',
    ...(pricingEnabled ? { pricing: emptyMatterPricing(pricingCurrency) } : {}),
  });

  const goPrev = useCallback(() => {
    if (currentIdx > 0) {
      setCurrentStep(STEPS[currentIdx - 1].key);
    } else {
      onBack();
    }
  }, [currentIdx, onBack]);

  // Capture form data and advance to next step
  const handleStepSubmit = useCallback((formData: Record<string, any>) => {
    setAccumulated((prev) => ({ ...prev, ...formData }));
    if (currentIdx < STEPS.length - 1) {
      setCurrentStep(STEPS[currentIdx + 1].key);
    }
  }, [currentIdx]);

  // Final submit on review step
  const handleFinalSubmit = useCallback((formData: Record<string, any>) => {
    const merged = { ...accumulated, ...formData };
    onSubmit({
      matterTitle: (merged.matterTitle || '').trim(),
      category: merged.category || '',
      clients: merged.clients || [],
      collaborators: merged.collaborators || [],
      filesOrDescription: merged.filesOrDescription || { files: [], description: '' },
      language: merged.language || '',
      jurisdiction: merged.jurisdiction || '',
      selectedIndexes: merged.selectedIndexes || [],
      dataSources: merged.dataSources || [],
      analysisOptions: merged.analysisOptions || DEFAULT_ANALYSIS_OPTIONS,
      pricing: merged.pricing,
    });
  }, [accumulated, onSubmit]);

  // Sync accumulated data when BaseForm values change (keeps accumulated in sync for back navigation)
  const handleValuesChange = useCallback((values: Record<string, any>) => {
    wizardDirtyRef.current = true;
    setAccumulated((prev) => ({ ...prev, ...values }));
  }, []);

  // Wrap onCancel with a confirmation modal if the user has entered any data
  const handleCancel = useCallback(() => {
    if (wizardDirtyRef.current || currentIdx > 0) {
      // Canonical GlobalModalPayload — the legacy `{ message, subMsg }` shape
      // this used renders the headline in the body slot and logs a dev warning.
      dispatch(
        showModal({
          showModal: true,
          type: 'confirmation',
          title: t('Discard Changes'),
          message: t('You have unsaved changes. Are you sure you want to discard them?'),
          variant: 'danger',
          icon: <PiTrashLight className="w-6 h-6 text-danger-500" />,
          confirmLabel: t('Discard'),
          onConfirm: () => {
            dispatch(hideModal());
            onCancel();
          },
          onCancel: () => {
            dispatch(hideModal());
          },
        } as any)
      );
    } else {
      onCancel();
    }
  }, [currentIdx, dispatch, onCancel, t]);

  // ─── Step configs for BaseForm ─────────────────────────────────────

  const stepTitles: Record<WizardStep, { title: string; description: string }> = {
    basics: {
      title: t('Matter Details'),
      description: t('Name your matter and set where it applies. Clients and collaborators can also be added later.'),
    },
    documents: {
      title: t('Documents'),
      description: t('Upload the documents PONS will analyze - contracts, pleadings, correspondence - or describe the matter in your own words.'),
    },
    research: {
      title: t('Legal Sources'),
      description: t('Choose which legal databases and knowledge bases PONS should search during analysis.'),
    },
    review: {
      title: t('Review & Launch'),
      description: t('Confirm your setup and choose which PONS features to run. Everything is enabled by default - toggle off anything you don\u2019t need.'),
    },
  };

  // Step 1: Basics - now includes language & jurisdiction
  const basicsSections: BaseFormSectionConfig[] = useMemo(() => [
    {
      fields: [
        {
          name: 'matterTitle',
          type: 'input',
          label: t('Matter Title'),
          placeholder: t('e.g. Smith v. Johnson, IP License Review, Tenant Dispute...'),
          required: true,
          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    },
    {
      gridCols: categories.length > 0 ? 3 : 2,
      fields: [
        ...(categories.length > 0 ? [{
          name: 'category',
          type: 'dropdown',
          label: t('Category'),
          options: categories,
          placeholder: t('Select'),
          customProps: { fullWidth: true },
          gridSize: 'third',
        } as BaseFormFieldConfig] : []),
        {
          name: 'language',
          type: 'dropdown',
          label: t('Language'),
          options: languages,
          // Defaults to the user's AI preference; overridable, and unset is a
          // valid answer — never a blocking requirement.
          placeholder: t('Select'),
          required: false,
          customProps: { fullWidth: true },
          gridSize: categories.length > 0 ? 'third' : 'half',
        } as BaseFormFieldConfig,
        {
          name: 'jurisdiction',
          type: 'dropdown',
          label: t('Jurisdiction'),
          options: jurisdictions,
          // Unset is a valid answer — the backend resolver owns the decision
          // when the user has no preference and picks nothing here.
          placeholder: t('Select Jurisdiction'),
          required: false,
          customProps: { fullWidth: true },
          gridSize: categories.length > 0 ? 'third' : 'half',
        } as BaseFormFieldConfig,
      ],
    },
    {
      title: t('Clients'),
      description: t('Select one or more clients for this matter. You can also add clients later.'),
      fields: [
        {
          name: 'clients',
          type: 'custom',
          // Empty label — the section heading above already says "Clients"; a
          // field label under it would only repeat it.
          label: '',

          customComponent: ClientSelector,
          customProps: {
            clientOptions,
            roleOptions: clientRoleOptions,
            onAddNewClient,
            autoAddClient,
          },
          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    },
    {
      title: t('Access'),
      description: t('Add people who can access this matter'),
      fields: [
        {
          name: 'collaborators',
          type: 'collaboratorSelector',
          // Empty label — the section heading above already says "Access".
          label: '',

          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    },
    ...(pricingEnabled ? [{
      title: t('Billing'),
      description: t('How this matter is billed. You can change this later from the matter.'),
      fields: [
        {
          name: 'pricing',
          type: 'custom',
          label: t('Pricing & Economics'),
          customComponent: WizardPricingField,
          customProps: { defaultCurrency: pricingCurrency },
          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    }] : []),
    ...(projectOptions.length > 0 ? [{
      fields: [
        {
          name: 'projectUUID',
          type: 'dropdown' as const,
          label: t('Link to Project'),
          placeholder: t('Select a project (optional)'),
          required: false,
          gridSize: 'full' as const,
          customProps: { fullWidth: true },
          options: projectOptions,
        } as BaseFormFieldConfig,
      ],
    }] : []),
  ], [categories, languages, jurisdictions, clientOptions, clientRoleOptions, onAddNewClient, autoAddClient, projectOptions, pricingEnabled, pricingCurrency, t]);

  const basicsSchema = useMemo(() => {
    const s = new FormSchema('WizardBasicsSchema');
    s.addField(new FormField('matterTitle', 'text', true, t('Matter Title'), {
      customValidator: (value: any) => {
        if (!value || !String(value).trim()) {
          return { isValid: false, error: t('Matter title is required') };
        }
        return { isValid: true, sanitized: String(value).trim() };
      },
    }));
    s.addField(new FormField('category', 'text', false, t('Category')));
    s.addField(new FormField('language', 'text', false, t('Language')));
    s.addField(new FormField('jurisdiction', 'text', false, t('Jurisdiction')));
    s.addField(new FormField('clients', 'text', false, t('Clients')));
    s.addField(new FormField('collaborators', 'text', false, t('Access')));
    if (pricingEnabled) {
      s.addField(new FormField('pricing', 'text', false, t('Pricing & Economics'), {
        customValidator: (value: any) => {
          const errs = getMatterPricingErrors(value || emptyMatterPricing(pricingCurrency));
          const firstKey = Object.keys(errs)[0] as keyof typeof errs | undefined;
          if (firstKey) return { isValid: false, error: t(errs[firstKey] as string) };
          return { isValid: true, sanitized: value };
        },
      }));
    }
    return s;
  }, [t, pricingEnabled, pricingCurrency]);

  // Step 2: Documents
  const documentsSections: BaseFormSectionConfig[] = useMemo(() => [
    {
      fields: [
        {
          name: 'filesOrDescription',
          type: 'custom',
          label: t('Files & Description'),
          customComponent: FileOrDescriptionSelector,
          required: true,
          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    },
  ], [t]);

  const documentsSchema = useMemo(() => {
    const s = new FormSchema('WizardDocumentsSchema');
    s.addField(new FormField('filesOrDescription', 'text', true, t('Files & Description'), {
      customValidator: (value: any) => {
        const v = value || {};
        const hasFiles = v.files && v.files.length > 0;
        const hasDesc = v.description && v.description.trim().length > 0;
        if (!hasFiles && !hasDesc) {
          return { isValid: false, error: t('Upload at least one document or provide a description') };
        }
        return { isValid: true };
      },
    }));
    return s;
  }, [t]);

  // Step 3: Sources - legal indexes + data sources (language/jurisdiction moved to step 1)
  const researchSections: BaseFormSectionConfig[] = useMemo(() => [
    {
      title: t('Legal Databases'),
      description: t('Select which official legal databases PONS should search. These include legislation, case law, preparatory works, and more - depending on your jurisdiction.'),
      fields: [
        {
          name: 'selectedIndexes',
          type: 'custom',
          label: t('Search Sources'),
          customComponent: IndexSourceSelector,
          customProps: {
            jurisdiction: accumulated.jurisdiction || defaultJurisdiction,
          },
          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    },
    ...(dataSourceOptions.length > 0 ? [{
      title: t('Your Data Sources'),
      description: t('Data sources are your own reusable knowledge bases that you upload to PONS and can use across multiple matters - for example, internal policies, property regulations, industry standards, or previous case research.'),
      fields: [
        {
          name: 'dataSources',
          type: 'dropdown',
          label: t('Data Sources'),
          options: dataSourceOptions,
          placeholder: t('Select data sources (optional)'),
          customProps: { multiple: true, fullWidth: true },
          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    }] : []),
  ], [dataSourceOptions, accumulated.jurisdiction, defaultJurisdiction, t]);

  const researchSchema = useMemo(() => {
    const s = new FormSchema('WizardResearchSchema');
    s.addField(new FormField('selectedIndexes', 'text', false, t('Search Sources')));
    s.addField(new FormField('dataSources', 'text', false, t('Data Sources')));
    return s;
  }, [t]);

  // Step 4: Review
  const reviewSummary = useMemo(() => ({
    title: accumulated.matterTitle || '',
    docCount: accumulated.filesOrDescription?.files?.length || 0,
    jurisdiction: accumulated.jurisdiction || '',
    jurisdictionLabel: jurisdictions.find((j) => j.value === accumulated.jurisdiction)?.label || accumulated.jurisdiction || '',
    language: accumulated.language || '',
    languageLabel: languages.find((l) => l.value === accumulated.language)?.label || accumulated.language || '',
    clientCount: accumulated.clients?.length || 0,
    collaboratorCount: accumulated.collaborators?.length || 0,
    indexCount: accumulated.selectedIndexes?.length || 0,
    dataSourceCount: accumulated.dataSources?.length || 0,
    category: categories.find((c) => c.value === accumulated.category)?.label || '',
  }), [accumulated, jurisdictions, languages, categories]);

  const reviewSections: BaseFormSectionConfig[] = useMemo(() => [
    {
      fields: [
        {
          name: 'analysisOptions',
          type: 'custom',
          label: t('Analysis Options'),
          customComponent: AnalysisOptionsPanel,
          customProps: {
            jurisdiction: accumulated.jurisdiction || defaultJurisdiction,
            hasDataSources: dataSourceOptions.length > 0,
            summary: reviewSummary,
          },
          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    },
  ], [accumulated.jurisdiction, defaultJurisdiction, dataSourceOptions, reviewSummary, t]);

  const reviewSchema = useMemo(() => {
    const s = new FormSchema('WizardReviewSchema');
    s.addField(new FormField('analysisOptions', 'text', false, t('Analysis Options')));
    return s;
  }, [t]);

  // ─── Per-step config ───────────────────────────────────────────────

  const isLastStep = currentIdx === STEPS.length - 1;

  const stepConfigs: Record<WizardStep, {
    sections: BaseFormSectionConfig[];
    schema: FormSchema;
    initialValues: Record<string, any>;
  }> = {
    basics: {
      sections: basicsSections,
      schema: basicsSchema,
      initialValues: {
        matterTitle: accumulated.matterTitle || '',
        category: accumulated.category || categories[0]?.value || '',
        language: accumulated.language || defaultLanguage || '',
        jurisdiction: accumulated.jurisdiction || defaultJurisdiction || '',
        clients: accumulated.clients || [],
        collaborators: accumulated.collaborators || [],
        projectUUID: accumulated.projectUUID || '',
      },
    },
    documents: {
      sections: documentsSections,
      schema: documentsSchema,
      initialValues: {
        filesOrDescription: accumulated.filesOrDescription || { files: [], description: '' },
      },
    },
    research: {
      sections: researchSections,
      schema: researchSchema,
      initialValues: {
        selectedIndexes: accumulated.selectedIndexes || [],
        dataSources: accumulated.dataSources || [],
      },
    },
    review: {
      sections: reviewSections,
      schema: reviewSchema,
      initialValues: {
        analysisOptions: accumulated.analysisOptions || DEFAULT_ANALYSIS_OPTIONS,
      },
    },
  };

  const config = stepConfigs[currentStep];

  // ─── Render ────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full">
      <WizardStepRail
        steps={STEPS}
        currentStep={currentStep}
        maxWidth={WIZARD_MAX_WIDTH}
        data-tour-id="wizard-step-indicator"
      />
      {/* No vertical padding on the band: `WizardStepTip` renders null once
          dismissed, and a padded wrapper around nothing leaves a 16px hole on
          the steps whose tip is gone but not on the others — the body would
          shift as the user paged through. The tip owns its own `mb-4`; the
          rail above owns the gap over it. */}
      {matterWizardStepTips[currentStep] && (
        <div className="px-6 flex-shrink-0">
          <WizardStepTip
            key={currentStep}
            tipId={`matter-wizard-${currentStep}`}
            title={matterWizardStepTips[currentStep].title}
            content={matterWizardStepTips[currentStep].content}
          />
        </div>
      )}
      <BaseForm
        key={currentStep}
        className="flex-1 min-h-0"
        title={stepTitles[currentStep].title}
        description={stepTitles[currentStep].description}
        sections={config.sections}
        schema={config.schema}
        initialValues={config.initialValues}
        onSubmit={isLastStep ? handleFinalSubmit : handleStepSubmit}
        onCancel={handleCancel}
        onBack={goPrev}
        submitLabel={isLastStep ? t('Create & Analyze') : t('Next')}
        cancelLabel={t('Cancel')}
        backLabel={currentIdx === 0 ? t('Back') : t('Previous')}
        maxWidth={WIZARD_MAX_WIDTH}
        isSubmitting={isLastStep ? isSubmitting : false}
        showConfirmationModal={false}
        onValuesChange={handleValuesChange}
      />
      <FeatureTour
        steps={currentTourSteps}
        run={isWizardTourRunning}
        onCallback={handleWizardTourCallback}
        disableOverlay
      />
    </div>
  );
};

export default MatterWizard;
